'use client'

import React, { createContext, useContext, useEffect, useState } from 'react'
import { useRouter } from 'next/navigation'
import { useSession } from 'next-auth/react'
import { toast } from 'sonner'
import { getPermissions } from '@/lib/actions/user/get-permissions'
import type { PermissionData } from '@/lib/types/permissions'

interface PermissionsContextType {
  permissions: PermissionData | null
  isLoading: boolean
  hasPermission: (module: string, action?: string) => boolean
  hasAnyReportPermission: () => boolean
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined)

export const PermissionsProvider = ({ children }: { children: React.ReactNode }) => {
  const { data: session, status } = useSession()
  const [isMounted, setIsMounted] = useState(false)
  const router = useRouter()

  useEffect(() => {
    setIsMounted(true)
  }, [])

  const permissions = (session?.permissions as PermissionData | null) || null
  const isLoading = status === 'loading' || !isMounted

  const hasPermission = (module: string, action: string = 'read'): boolean => {
    if (isLoading) return false
    if (!permissions) return false
    if (!permissions.is_enabled) return true

    const moduleData = permissions.data[module]
    if (!moduleData) return false

    if (module === 'report') {
      return (moduleData as any)[action] === true
    }

    return (moduleData as any)[action] === true
  }

  const hasAnyReportPermission = (): boolean => {
    if (isLoading) return false
    if (!permissions) return false
    if (!permissions.is_enabled) return true

    const reports = permissions.data.report
    if (!reports) return false

    return Object.values(reports).some((val) => val === true)
  }

  // Define route to permission mapping
  const routePermissions: Record<string, { module: string; action: string }> = {
    '/stock-report': { module: 'report', action: 'stock_report' },
    '/order-items': { module: 'report', action: 'order_item_report' },
    '/order-taxes': { module: 'report', action: 'order_taxes_report' },
    '/sale': { module: 'sales', action: 'write' },
    '/orders': { module: 'order', action: 'read' },
    '/saved-orders': { module: 'order', action: 'read' },
  }

  useEffect(() => {
    if (isLoading || !permissions || !permissions.is_enabled) return

    const path = window.location.pathname
    const permission = routePermissions[path]

    if (permission) {
      const allowed = hasPermission(permission.module, permission.action)
      if (!allowed) {
        toast.error("You don't have permission to access this page")
        router.push('/')
      }
    }
  }, [permissions, isLoading, router])

  return (
    <PermissionsContext.Provider
      value={{ permissions, isLoading, hasPermission, hasAnyReportPermission }}
    >
      {children}
    </PermissionsContext.Provider>
  )
}

export const usePermissions = () => {
  const context = useContext(PermissionsContext)
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionsProvider')
  }
  return context
}
